import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Award, Download, ArrowLeft, Zap, Copy, Share2 } from "lucide-react";

export default function Certificate({ shareMode = false }) {
  const params = useParams();
  const idOrToken = shareMode ? params.token : params.id;
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    const path = shareMode
      ? `/certificates/share/${idOrToken}`
      : `/attempts/${idOrToken}`;
    api.get(path)
      .then((r) => setAttempt(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Certificate not found"));
  }, [idOrToken, shareMode]);

  const print = () => window.print();

  const shareUrl = attempt?.share_token
    ? `${window.location.origin}/c/${attempt.share_token}`
    : "";

  const shareText = attempt
    ? `I just earned a StenoSync certificate — ${attempt.accuracy_percent}% accuracy at ${attempt.wpm} WPM on "${attempt.item_title || "a practice exercise"}"!`
    : "";

  const copyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard?.writeText(shareUrl);
    toast.success("Share link copied");
  };

  const shareTo = (target) => {
    if (!shareUrl) return;
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(shareText);
    let href = "";
    if (target === "whatsapp") href = `https://wa.me/?text=${text}%20${url}`;
    else if (target === "linkedin") href = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    else if (target === "twitter") href = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-card gap-4">
        <div className="font-mono text-red-700">{error}</div>
        <Link to="/" className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold">Back to StenoSync</Link>
      </div>
    );
  }
  if (!attempt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-card font-mono text-muted-foreground">Loading certificate…</div>
    );
  }

  const qualifies = attempt.accuracy_percent >= 90 && attempt.wpm >= 100;
  const dateStr = new Date(attempt.created_at).toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
  const moduleLabel = {
    dictation: "Audio Dictation",
    legal: "Legal Formatting Typing",
    pitman: "Pitman Steno Exercise",
  }[attempt.module] || attempt.module;

  return (
    <div className="min-h-screen bg-secondary/60 py-10 px-6 print:bg-card print:p-0" data-testid="certificate-page">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .cert-frame { border: 6px double #0A2540 !important; box-shadow: none !important; }
        }
      `}</style>

      {/* Top bar */}
      <div className="no-print max-w-5xl mx-auto flex items-center justify-between mb-6 flex-wrap gap-3">
        <Link
          to={shareMode ? "/" : "/history"}
          className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-gold"
          data-testid="cert-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> {shareMode ? "Back to StenoSync" : "Back to History"}
        </Link>
        {qualifies && (
          <div className="flex items-center gap-2 flex-wrap">
            {!shareMode && (
              <>
                <Button
                  onClick={() => setShareOpen((s) => !s)}
                  variant="outline"
                  className="rounded-lg h-11 border-border text-xs uppercase tracking-[0.2em] font-bold"
                  data-testid="cert-share-toggle"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              </>
            )}
            <Button
              onClick={print}
              className="rounded-lg h-11 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="cert-print-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Print / Save as PDF
            </Button>
          </div>
        )}
      </div>

      {/* Share panel */}
      {qualifies && shareOpen && !shareMode && (
        <div className="no-print max-w-5xl mx-auto mb-6 border-2 border-brand-gold/40 bg-card p-6" data-testid="cert-share-panel">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">Share your achievement</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[240px] border border-border bg-secondary/60 px-3 py-2 font-mono text-sm truncate" data-testid="cert-share-url">
              {shareUrl}
            </div>
            <Button onClick={copyLink} variant="outline" className="rounded-lg border-border" data-testid="cert-copy-link">
              <Copy className="w-4 h-4 mr-2" /> Copy link
            </Button>
            <Button
              onClick={() => shareTo("whatsapp")}
              className="rounded-lg bg-[#25D366] hover:bg-[#1FB855] text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="cert-share-whatsapp"
            >
              WhatsApp
            </Button>
            <Button
              onClick={() => shareTo("linkedin")}
              className="rounded-lg bg-[#0A66C2] hover:bg-[#004182] text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="cert-share-linkedin"
            >
              LinkedIn
            </Button>
            <Button
              onClick={() => shareTo("twitter")}
              className="rounded-lg bg-[#0A2540] hover:bg-black text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="cert-share-twitter"
            >
              X / Twitter
            </Button>
          </div>
          <div className="mt-3 text-[10px] font-mono text-muted-foreground">
            Anyone with this link can view (and verify) your certificate — but only you can print or share it further.
          </div>
        </div>
      )}

      {/* Not qualified banner */}
      {!qualifies && (
        <div className="no-print max-w-5xl mx-auto mb-6 border-l-4 border-yellow-500 bg-yellow-50 px-6 py-4" data-testid="cert-not-qualified">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-yellow-800">Certificate locked</div>
          <div className="font-mono text-sm text-yellow-900 mt-1">
            You need at least <strong>90% accuracy</strong> AND <strong>100 WPM</strong> to earn a certificate.
            This attempt scored <strong>{attempt.accuracy_percent}%</strong> at <strong>{attempt.wpm} WPM</strong>.
          </div>
        </div>
      )}

      {/* Certificate frame */}
      <div className="cert-frame max-w-5xl mx-auto bg-card p-16 border-4 border-brand-gold relative shadow-2xl">
        <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-brand-gold/40" />
        <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-brand-gold/40" />
        <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-brand-gold/40" />
        <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-brand-gold/40" />

        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand-gold flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-black text-2xl tracking-tight">StenoSync</span>
        </div>

        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.4em] font-bold text-brand-gold mb-3">Certificate of Achievement</div>
          <Award className="w-14 h-14 mx-auto mb-6 text-brand-gold" strokeWidth={1.5} />

          <div className="text-sm font-mono text-muted-foreground mb-4">This certifies that</div>
          <h1 className="text-5xl font-black tracking-tight mb-4" data-testid="cert-name">{attempt.user_name || "Student"}</h1>

          <div className="text-sm font-mono text-muted-foreground mb-2">has successfully completed the exercise</div>
          <div className="text-xl font-black tracking-tight mb-8" data-testid="cert-title">
            "{attempt.item_title || "Practice Exercise"}"
          </div>

          <div className="text-sm font-mono text-muted-foreground mb-2">in the {moduleLabel} module with</div>

          <div className="inline-flex items-stretch border-2 border-brand-gold my-4">
            <div className="px-8 py-4 border-r-2 border-brand-gold">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Accuracy</div>
              <div className="text-3xl font-black font-mono text-green-700" data-testid="cert-accuracy">{attempt.accuracy_percent}%</div>
            </div>
            <div className="px-8 py-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Speed</div>
              <div className="text-3xl font-black font-mono text-brand-gold" data-testid="cert-wpm">{attempt.wpm} <span className="text-sm">wpm</span></div>
            </div>
          </div>

          {qualifies ? (
            <div className="mt-6 text-sm font-mono text-foreground/80">
              Exceeding the mastery threshold of <strong>90% accuracy at 100+ WPM</strong>.
            </div>
          ) : (
            <div className="mt-6 text-sm font-mono text-yellow-800">
              (Preview only — reach 90% accuracy at 100+ WPM to unlock printing.)
            </div>
          )}

          <div className="mt-12 grid grid-cols-2 gap-16">
            <div className="border-t-2 border-brand-gold pt-3">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Date awarded</div>
              <div className="font-mono text-sm mt-1" data-testid="cert-date">{dateStr}</div>
            </div>
            <div className="border-t-2 border-brand-gold pt-3">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Verification</div>
              <div className="font-mono text-xs mt-1 truncate">CERT-{(attempt.id || attempt.share_token || "").slice(-10).toUpperCase()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
